/**
 * Passport share deep-links.
 *
 * The /p/:token page lives behind a recruiter login (RecruiterGuard). This
 * client resolves a token into the cv_id + candidate prefs the page needs to
 * render the normal recruiter passport view. The request is authenticated
 * (apiCall injects the recruiter's token); the gateway additionally enforces
 * the recruiter role and profile-service re-checks the candidate's live
 * visibility (career_status != invisible, not deleted, CV exists, link valid).
 */
import type { RecruiterPrefs } from '../components/Passport/RecruiterPassportViews';

type ApiCall = (url: string, options?: RequestInit) => Promise<Response>;

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * `ok`          — resolve succeeded, render the passport
 * `unavailable` — candidate switched to invisible / deleted / CV gone
 * `expired`     — link ran out or was revoked
 * `invalid`     — token unknown (404)
 * `forbidden`   — caller is not a recruiter (should not happen behind the guard)
 * `error`       — network/server problem, worth a retry
 */
export type ShareStatus =
  | 'ok'
  | 'unavailable'
  | 'expired'
  | 'invalid'
  | 'forbidden'
  | 'error';

export interface ResolvedShareLink {
  status: ShareStatus;
  cvId?: string | null;
  candidateId?: string | null;
  prefs?: RecruiterPrefs | null;
  expiresAt?: number | null;
}

export async function resolveShareLink(
  apiCall: ApiCall,
  token: string,
): Promise<ResolvedShareLink> {
  let resp: Response;
  try {
    resp = await apiCall(`${API_BASE}/api/v1/passport/share/${encodeURIComponent(token)}`);
  } catch {
    return { status: 'error' };
  }
  if (resp.status === 404) return { status: 'invalid' };
  if (resp.status === 401) return { status: 'error' };
  if (resp.status === 403) return { status: 'forbidden' };
  if (!resp.ok) return { status: 'error' };

  const data = await resp.json();
  const status = (data?.status as ShareStatus) || 'error';
  if (status !== 'ok') return { status };

  return {
    status: 'ok',
    cvId: data?.cv_id ?? null,
    candidateId: data?.candidate_id ?? null,
    prefs: {
      career_status: data?.career_status ?? null,
      base_locations: data?.base_locations ?? null,
      preferred_locations: data?.preferred_locations ?? null,
      role_preferences: data?.role_preferences ?? null,
      current_role: data?.current_role ?? null,
      current_level: data?.current_level ?? null,
    },
    expiresAt: data?.expires_at ?? null,
  };
}
